import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { REFRESH_USER, SET_MUST_CHANGE_PASSWORD } from 'constants/types';
import { showAlert } from 'state/actions/site';
import clearMustChangePasswordClaim from './clearMustChangePasswordClaim';

/**
 * handles the forced password change for users created with a temporary password:
 * re-authenticates with the temporary password, updates it, then clears the
 * mustChangePassword custom claim so the portal unlocks
 */
export default function useForceChangePassword() {
  const dispatch = useDispatch();
  const { zohoData } = useSelector(state => state.user);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // once the password is updated, a retry only needs to redo the claim clearing
  const [passwordAlreadyChanged, setPasswordAlreadyChanged] = useState(false);

  // returns true when the given password is valid for the current user
  async function reauthenticate(user, currentPassword) {
    try {
      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await user.reauthenticateWithCredential(credential);
      return true;
    } catch (error) {
      // MFA-enrolled users throw this code even when the password IS correct;
      // updatePassword still works because the MFA login just completed
      if (error.code === 'auth/multi-factor-auth-required') return true;
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: 'Wrong password' });
        return false;
      }
      console.error(error);
      setErrors({
        general: 'There was an error while validating your credentials'
      });
      return false;
    }
  }

  // returns true when the new password was applied on the firebase user
  async function applyNewPassword(user, newPassword) {
    try {
      await user.updatePassword(newPassword);
      return true;
    } catch (error) {
      if (error.code === 'auth/weak-password') {
        setErrors({ newPassword: 'Password is too weak' });
        return false;
      }
      if (error.code === 'auth/requires-recent-login') {
        setErrors({
          general: 'Your session has expired. Please log out and log in again.'
        });
        return false;
      }
      console.error(error);
      setErrors({
        general: 'There was an error while changing your password'
      });
      return false;
    }
  }

  // clears the claim and refreshes local state; the view redirects once the flag flips
  async function unlockPortal(user, newPassword) {
    try {
      await clearMustChangePasswordClaim();
      // force-refresh the token so it no longer carries the claim
      await user.getIdToken(true);
      dispatch({
        type: REFRESH_USER,
        payload: { ...zohoData, Portal_Password: newPassword }
      });
      dispatch(
        showAlert({ message: 'Password Updated Successfully', code: 'success' })
      );
      dispatch({ type: SET_MUST_CHANGE_PASSWORD, payload: false });
    } catch (error) {
      console.error(error);
      setErrors({
        general:
          'Your password was updated but we could not unlock the portal. Please submit again.'
      });
    }
  }

  /**
   * runs the full forced change flow
   * @param currentPassword - the temporary (or current) password
   * @param newPassword - the new password to set
   */
  async function submit(currentPassword, newPassword) {
    setErrors({});
    setLoading(true);
    const user = firebase.auth().currentUser;
    try {
      if (!passwordAlreadyChanged) {
        const reauthenticated = await reauthenticate(user, currentPassword);
        if (!reauthenticated) return;
        const passwordApplied = await applyNewPassword(user, newPassword);
        if (!passwordApplied) return;
        setPasswordAlreadyChanged(true);
      }
      await unlockPortal(user, newPassword);
    } finally {
      setLoading(false);
    }
  }

  return { loading, errors, submit };
}
